// src/components/stages/BillingStage.tsx
// Generates this visit's OPD service bill before the visit can close. The
// lines come from the hospital's OPD fee schedule (Admin → Billing & Insurance
// → OPD Fee Schedule), priced for this branch and the visit doctor's
// department; Registration is added only for a patient's first visit (the
// schedule's "applies to" setting — it used to be hard-coded here).
// Any pharmacy charges from the Pharmacy stage just before this are billed
// separately there (dispensing bills itself inline) — this stage only covers
// the OPD service lines. Only reached for OP visits — IP-bound visits skip
// straight past this.
import { useState } from 'react';
import { createOPDBill } from '../../api';
import { useBillingSettings } from '../../hooks/useBillingSettings';
import type { StageProps } from '../../types/visit';
import { StageLayout } from '../ui/StageLayout';
import { StageActionBar } from '../ui/StageActionBar';
import {
  OpdFeeLinesEditor, billTotals, lineProblems, toBillItems, useOpdBillLines,
} from '../../features/opd-fees/OpdFeeLines';

type PaymentMode = 'Cash' | 'UPI' | 'Card';
type PaymentStatus = 'Paid' | 'Pending';

export default function BillingStage({ visit, patient, completeStage, loading }: Pick<StageProps, 'visit' | 'patient' | 'completeStage' | 'loading'>) {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { quoteQuery, quote, lines, setLines } = useOpdBillLines({ visitId: visit?.id ?? null }, !!visit?.id);
  const [payMode, setPayMode]       = useState<PaymentMode>('Cash');
  const [payStatus, setPayStatus]   = useState<PaymentStatus>('Paid');
  const [creating, setCreating]     = useState(false);
  const [error, setError]           = useState('');

  const consultationId = visit?.stages?.find(s => s.stage === 'consultation')?.stage_data?.consultation_id || null;

  const lineItems = toBillItems(lines, quote, gstEnabled);
  const problems = lineProblems(lines, quote);
  const { subtotal: sub, discount: discountAmt, gst: gstAmt, total } = billTotals(lineItems);
  const ready = lineItems.length > 0 && problems.length === 0;

  const generate = async () => {
    if (!ready || creating) return;
    setCreating(true); setError('');
    try {
      const bill = await createOPDBill({
        patient_id: patient?.id, visit_id: visit.id, consultation_id: consultationId,
        items: lineItems, payment_mode: payMode, payment_status: payStatus,
      });
      completeStage('billing', { bill_id: bill.id, bill_number: bill.bill_number, total_amount: bill.total_amount });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setCreating(false);
  };

  const ctx = quote?.context;

  return (
    <div className="fade-in flex flex-col gap-3.5">
    <StageLayout variant="single">
    <div className="card">
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>🧾 Billing</div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Generate the OPD bill for {patient?.name || visit?.name} before closing this visit</div>

      {ctx && (
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 rounded-(--radius) bg-canvas px-3 py-2 text-[12px] text-ink-muted">
          <span>Department: <b className="text-ink">{ctx.specialty_name || 'Not set — default fees'}</b>{ctx.doctor_name ? ` (${ctx.doctor_name})` : ''}</span>
          <span>Patient: <b className="text-ink">{ctx.is_new_patient ? 'First visit' : 'Returning'}</b></span>
        </div>
      )}

      {error && <div style={{ padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', color: '#991B1B', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {quoteQuery.isLoading ? (
        <div className="py-6 text-center text-[13px] text-ink-muted">Loading the fee schedule…</div>
      ) : quoteQuery.isError || !quote ? (
        <div className="rounded-(--radius) bg-danger-soft px-3 py-2 text-[13px] text-danger-ink">
          Could not load the OPD fee schedule. <button type="button" className="underline" onClick={() => quoteQuery.refetch()}>Try again</button>
        </div>
      ) : (
        <OpdFeeLinesEditor quote={quote} lines={lines} onChange={setLines} gstEnabled={gstEnabled} gstOptions={gstPercentageOptions} />
      )}

      {problems.length > 0 && (
        <div className="mt-3 rounded-(--radius) bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">{problems.join(' · ')}</div>
      )}

      <div style={{ padding: 12, background: '#F9FAFB', borderRadius: 'var(--radius)', marginBottom: 14, marginTop: 14 }}>
        {(
          [
            ['Subtotal', '₹' + sub.toFixed(2)],
            ...(discountAmt > 0 ? [['Discount', '− ₹' + discountAmt.toFixed(2)]] : []),
            ...(gstEnabled ? [['GST', '₹' + gstAmt.toFixed(2)]] : []),
            ['Total', '₹' + total.toFixed(2)],
          ] as Array<[string, string]>
        ).map(([l, v], i, arr) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: i < arr.length - 1 ? 6 : 0, fontWeight: i === arr.length - 1 ? 700 : 400, fontSize: i === arr.length - 1 ? 16 : 13, borderTop: i === arr.length - 1 ? '1px solid #E5E7EB' : 'none', paddingTop: i === arr.length - 1 ? 8 : 0 }}>
            {l}<span>{v}</span>
          </div>
        ))}
      </div>

      <div className="grid2" style={{ marginBottom: 14 }}>
        <div>
          <label className="lbl">Payment mode</label>
          <select value={payMode} onChange={e => setPayMode(e.target.value as PaymentMode)}>
            {(['Cash', 'UPI', 'Card'] as const).map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="lbl">Payment status</label>
          <select value={payStatus} onChange={e => setPayStatus(e.target.value as PaymentStatus)}>
            <option value="Paid">Paid now</option>
            <option value="Pending">Pending — bill now, collect later</option>
          </select>
        </div>
      </div>

    </div>
    </StageLayout>
    <StageActionBar
      readiness={!lineItems.length ? 'Add at least one billable line to continue' : problems.length ? 'Fix the highlighted charges' : '✓ Ready to generate bill'}
      readinessTone={ready ? 'success' : 'warn'}
    >
      <button className="btn-primary" disabled={!ready || creating || loading} onClick={generate}>
        {creating ? 'Generating…' : 'Generate bill & continue →'}
      </button>
    </StageActionBar>
    </div>
  );
}
